/**
 * DataTable molecule — TanStack table + optional `renderToolbar(table)` + pagination.
 * Same `table` reference is used for the toolbar and the grid; `DataTableOrganism` composes on top without forking that contract.
 */
import { useState } from "react";
import type {
  ColumnFiltersState,
  Row,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { cn } from "@/utils/cn";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../atoms/Table";
import { PaginationBar } from "../PaginationBar";
import type { DataTableProps } from "./DataTable.types";
import {
  dataTableBodyCellClass,
  dataTableEmptyCellClass,
  dataTableFooterPaginationClass,
  getDataTableFrameClassName,
  dataTableHeadCellClass,
  dataTableHeaderSectionClass,
  getDataTableBodyClassName,
  getDataTableBodyRowClass,
} from "./DataTable.styles";
import {
  buildGlobalFilterFn,
  dataTableDefaultColumnSizing,
} from "./DataTable.utils";

export function DataTable<TData>({
  columns,
  data,
  pagination = false,
  pageSize = 10,
  onRowClick,
  className,
  renderToolbar,
  emptyState,
  globalSearchKeys,
  globalFilter: globalFilterControlled,
  onGlobalFilterChange,
  columnFilters: columnFiltersControlled,
  onColumnFiltersChange,
  fillHeight = false,
  footerSlot,
  initialColumnVisibility,
  paginationItemLabel = "rows",
  enableRowSelection = true,
  getRowId,
  rowSelection: rowSelectionControlled,
  onRowSelectionChange,
  framed = true,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialColumnVisibility ?? {}
  );
  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>({});
  const [internalGlobalFilter, setInternalGlobalFilter] = useState("");

  const isColumnFiltersControlled = columnFiltersControlled !== undefined;
  const columnFilters = isColumnFiltersControlled
    ? columnFiltersControlled
    : internalColumnFilters;

  const isRowSelectionControlled = rowSelectionControlled !== undefined;
  const rowSelection = isRowSelectionControlled
    ? rowSelectionControlled
    : internalRowSelection;

  const isGlobalControlled = globalFilterControlled !== undefined;
  const globalFilterValue = isGlobalControlled
    ? (globalFilterControlled ?? "")
    : internalGlobalFilter;

  const globalFilterFn =
    globalSearchKeys && globalSearchKeys.length > 0
      ? buildGlobalFilterFn<TData>(globalSearchKeys)
      : buildGlobalFilterFn<TData>();

  const table = useReactTable({
    data,
    columns,
    defaultColumn: dataTableDefaultColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn,
    onSortingChange: setSorting,
    onColumnFiltersChange: isColumnFiltersControlled
      ? (updater) => {
          const next =
            typeof updater === "function"
              ? updater(columnFilters)
              : updater;
          onColumnFiltersChange?.(next);
        }
      : setInternalColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection,
    getRowId,
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater;
      if (!isRowSelectionControlled) {
        setInternalRowSelection(next);
      }
      onRowSelectionChange?.(next);
    },
    onGlobalFilterChange: isGlobalControlled
      ? (updater) => {
          const next =
            typeof updater === "function"
              ? updater(globalFilterValue)
              : updater;
          onGlobalFilterChange?.(String(next ?? ""));
        }
      : setInternalGlobalFilter,
    initialState: {
      pagination: {
        pageSize,
      },
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: globalFilterValue,
    },
  });

  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount() || 1;
  const totalItems = table.getFilteredRowModel().rows.length;

  const tableBody = table.getRowModel().rows;
  const showEmptyState = !tableBody?.length;

  return (
    <div
      className={cn(
        fillHeight ? "flex flex-col gap-4 h-full min-h-0" : "space-y-4",
        className
      )}
    >
      {renderToolbar ? renderToolbar(table) : null}

      <div
        className={cn(
          getDataTableFrameClassName(framed),
          fillHeight && "flex flex-1 min-h-0 flex-col"
        )}
      >
        <Table
          scrollX
          scrollY={fillHeight}
          // fillHeight tables fill their frame width (columns distribute to fit,
          // respecting per-cell minWidth) so no spurious horizontal scrollbar runs
          // across the sticky header; auto-height tables keep max-content sizing.
          className={cn("min-w-full", fillHeight ? "w-full" : "w-max")}
          containerClassName={fillHeight ? "flex-1 min-h-0" : undefined}
        >
          <TableHeader className={dataTableHeaderSectionClass}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} inHeader>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{
                      minWidth: header.column.columnDef.minSize,
                      width: header.getSize(),
                    }}
                    className={cn(
                      dataTableHeadCellClass,
                      fillHeight && "sticky top-0 z-10 bg-white"
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className={getDataTableBodyClassName(pagination)}>
            {showEmptyState ? (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className={dataTableEmptyCellClass}
                >
                  {emptyState ?? "No results."}
                </TableCell>
              </TableRow>
            ) : (
              tableBody.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => onRowClick?.(row as Row<TData>)}
                  className={getDataTableBodyRowClass(!!onRowClick)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        minWidth: cell.column.columnDef.minSize,
                        width: cell.column.getSize(),
                      }}
                      className={dataTableBodyCellClass}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {pagination && footerSlot ? footerSlot(table) : null}

        {pagination && !footerSlot ? (
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={(page) => table.setPageIndex(page - 1)}
            itemLabel={paginationItemLabel}
            className={cn(dataTableFooterPaginationClass, "border-dark-invirtus-soft")}
          />
        ) : null}
      </div>
    </div>
  );
}
